import { rootLinks } from '@map/components/app/links';
import { useUpdateAuthenticationFlow } from '@map/hooks/flows/authentication';
import { useCreateUser } from '@map/hooks/userApi';
import { windowExtended } from '@map/types';
import useMountEffect from '@shared/hooks/useMountEffect';
import useUrlQueryValue from '@shared/hooks/useUrlQueryValue';
import { sanitizePhoneNumber } from '@shared/util/phoneNumber';
import SlideRoutes from '@ui/mobile/SlideRoutes';
import { useCallback, useState } from 'react';
import { Route, useHistory, useLocation } from 'react-router-dom';
import { guestLinks } from '../../links';
import AccessCode from './Steps/AccessCode';
import Email from './Steps/Email';
import Menu from './Steps/Menu';
import Phone from './Steps/Phone';
import PhoneVerification from './Steps/PhoneVerification';
import { RegistrationContextProvider, Values } from './context';

const emptyRegistration = {
  email: '',
  password: '',
  phone: '',
  phoneVerificationCode: '',
};

const Registration = () => {
  const createUser = useCreateUser();
  const updateAuthenticationFlow = useUpdateAuthenticationFlow();
  const location = useLocation();
  const history = useHistory();
  const role = useUrlQueryValue('role') ?? 'beneficiary';
  const [values, setValues] = useState({ ...emptyRegistration, role });

  useMountEffect(() => {
    if (role && role !== 'beneficiary') {
      sessionStorage.setItem('role', role);
    }
  });

  const PUBLIC_REGISTRATION_TYPES = ['all_profiles', 'public_access'];
  const isPublicAccess = windowExtended.project.profile_access === 'public_access';
  const isSkillSurvey = windowExtended.project.project_type === 'skill_survey';

  const steps =
    isPublicAccess && isSkillSurvey
      ? [
          {
            path: guestLinks.register(),
            component: Email,
          },
        ]
      : [
          {
            path: guestLinks.register(),
            component: PUBLIC_REGISTRATION_TYPES.includes(windowExtended.project.profile_access)
              ? Menu
              : AccessCode,
          },
          { path: guestLinks.registerMenu(), component: Menu },
          { path: guestLinks.registerEmail(), component: Email },
          { path: guestLinks.registerPhone(), component: Phone },
          { path: guestLinks.registerPhoneVerify(), component: PhoneVerification },
        ];

  const updateValues = useCallback(
    (newValues: Partial<Values>) => setValues((prev) => ({ ...prev, ...newValues })),
    [],
  );
  const submit = useCallback(
    async (newValues?: Partial<Values>) => {
      const userData = { ...values, ...newValues };
      setValues(userData);
      const request = {
        access_code: windowExtended.project.access_code ?? '',
        project_slug: windowExtended.project.slug,
        password: userData.password,
        ...(userData.phone
          ? {
              phone: sanitizePhoneNumber(userData.phone),
              phone_verification_code: userData.phoneVerificationCode,
            }
          : { email: userData.email }),
      };
      const result = await createUser(request, windowExtended.invitation?.token);
      if (result.success) {
        updateAuthenticationFlow({ action: 'register' });
        history.push(rootLinks.root());
      }
      return result;
    },
    [createUser, history, updateAuthenticationFlow, values],
  );
  if (
    !windowExtended.project.access_code &&
    !PUBLIC_REGISTRATION_TYPES.includes(windowExtended.project.profile_access) &&
    location.pathname.startsWith(guestLinks.register()) &&
    location.pathname !== guestLinks.register()
  ) {
    history.replace(guestLinks.register());
  }
  return (
    <RegistrationContextProvider
      value={{
        values,
        updateValues,
        submit,
      }}
    >
      <SlideRoutes location={history.location}>
        {steps.map(({ path, component }) => (
          <Route key={path} path={path} exact component={component} />
        ))}
      </SlideRoutes>
    </RegistrationContextProvider>
  );
};

export default Registration;
